import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  routes,
  type ListingRun,
  type RunsListResponse,
  type ListingStatus,
  relistRunInboxStatusSchema,
  type RunOrigin,
  type RunDetailResponse,
  type RunFacetsResponse,
  type RunFeedPreviewResponse,
  type RunItemVerdict,
  type RunSetAsideListResponse,
  type RunStatusSummary,
  type RunSkuPreviewResponse,
  type SetAsideNoteResponse,
} from "@nova-ai/shared"

import { apiFetch } from "../lib/api"
import {
  restrictedPreviewData,
  previewFreshness,
  previewPollInterval,
} from "../lib/run-preview-freshness"
import { queryKeys } from "./query-keys"
import { previewFreshnessSinceRecovery, useRunPreviewRecovery } from "./use-run-preview-recovery"

export type RunsFilter = {
  status?: ListingStatus | undefined
  marketplace?: string | undefined
  startedBy?: string | undefined
  /** true is archived-only; "all" includes both. Default hides archives. */
  archived?: boolean | "all" | undefined
  origin?: Exclude<RunOrigin, "qa"> | undefined
}

export function useRuns(filter: RunsFilter) {
  return useQuery({
    queryKey: queryKeys.runs(filter),
    queryFn: async () => {
      const query: Record<string, string> = {}
      if (filter.status) {
        query.status = filter.status
      }
      if (filter.marketplace) {
        query.marketplace = filter.marketplace
      }
      if (filter.startedBy) {
        query.started_by = filter.startedBy
      }
      if (filter.archived) {
        query.archived = filter.archived === "all" ? "all" : "true"
      }
      if (filter.origin) {
        query.origin = filter.origin
      }

      const result = await apiFetch<RunsListResponse>(routes.runs.list(), {
        query,
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }

      return result.value
    },
  })
}

export const relistRunInboxQueryOptions = {
  queryKey: queryKeys.relistRunInbox,
  queryFn: async ({ signal }: { signal: AbortSignal }) => {
    const result = await apiFetch<unknown>(routes.runs.automaticInbox(), { signal })
    if (!result.ok) {
      throw new Error(result.error.message)
    }
    return relistRunInboxStatusSchema.parse(result.value)
  },
}

export function useRelistRunInboxStatus() {
  return useQuery(relistRunInboxQueryOptions)
}

// Feed preview and products are the two heavy run-detail reads (each renders
// or mirrors every SKU in the run). Hold them stale for a minute so navigating
// away and back doesn't re-pay that cost; state-changing mutations still call
// invalidateQueries, which forces an immediate refetch regardless of staleTime.
const RUN_DETAIL_STALE_MS = 60_000

// The listing-run header: the run row (incl. its derived caches —
// feedGapCount, masterCategory, simpleCount, all refreshed by validate) + a
// server-computed verdict summary (config count + OK/NOK/pending + NOK reason
// histogram). One lean call, no per-SKU rows and no live mirror read. The
// config-SKU list (CLI copy-apply gate) lives at GET /:slug/config-skus.
export function useRun(slug: string) {
  return useQuery({
    queryKey: queryKeys.runDetail(slug),
    enabled: !!slug,
    staleTime: RUN_DETAIL_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<RunDetailResponse>(routes.runs.detail({ slug }))
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

export function useRunStatus(reference: string) {
  return useQuery({
    queryKey: queryKeys.runStatus(reference),
    enabled: Boolean(reference),
    staleTime: RUN_DETAIL_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<RunStatusSummary>(routes.runs.status({ slug: reference }))
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

// The run page's category facets: the distinct marketplace + Magento categories
// present in the run (with counts) that populate the filter dropdowns. One lean
// run-wide read, fetched alongside the header like the reconcile banner.
export function useRunFacets(slug: string) {
  return useQuery({
    queryKey: queryKeys.runFacets(slug),
    enabled: !!slug,
    staleTime: RUN_DETAIL_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<RunFacetsResponse>(routes.runs.facets({ slug }))
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

// The run's set-aside bucket: the configs an operator manually parked out of the
// run (reason + who + when). Lazy — only fetched while the drawer is open
// (`enabled`), since the count the header shows already comes from the run
// detail summary, so the closed page pays nothing for it.
export function useRunSetAside(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.runSetAside(slug),
    enabled: enabled && !!slug,
    staleTime: RUN_DETAIL_STALE_MS,
    queryFn: async () => {
      const result = await apiFetch<RunSetAsideListResponse>(routes.runs.setAside({ slug }))
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

// API search/category filters choose candidates before pagination. Verdict,
// reason, and cohort filters narrow each inspected page afterward. Dashboard
// verdict tabs share those candidate pages and filter loaded evidence locally.
// Null is accepted (not just undefined) so the page can pass its `string | null`
// filter state straight through without a `?? undefined` dance at every call.
type FeedPreviewFilters = {
  // NOK-reason chip — narrows the NOK list to configs carrying one verdict reason.
  reason?: string | null | undefined
  // Config-SKU / product-name search.
  search?: string | null | undefined
  // Assigned marketplace category code.
  marketplaceCategory?: string | null | undefined
  // Magento (source) category leaf.
  magentoCategoryLeaf?: string | null | undefined
  // Keep only configs with at least one fixable gap.
  gapsOnly?: boolean | undefined
  // Keep only configs parked in the run's set-aside bucket.
  setAsideOnly?: boolean | undefined
  // Narrow the NOK list to one non-parked cohort (Fixable vs Excluded).
  nokCohort?: "fixable" | "excluded" | undefined
}

// Classic pagination over config SKUs: each fetched page renders `pageSize`
// candidate configs server-side (bounded mirror read + small payload); "load more" pulls
// the next page and the caller appends it. Every filter is part of the query
// key, so changing any of them (or `pageSize`) refetches from the first page.
export function useRunFeedPreview(
  slug: string,
  pageSize: number,
  verdict?: RunItemVerdict,
  filters: FeedPreviewFilters = {}
) {
  const client = useQueryClient()
  const {
    reason,
    search,
    marketplaceCategory,
    magentoCategoryLeaf,
    gapsOnly,
    setAsideOnly,
    nokCohort,
  } = filters
  const preview = useInfiniteQuery({
    // Retrying a timed-out page repeats expensive work and doubles the wait.
    retry: false,
    queryKey: [
      ...queryKeys.runFeedPreview(slug),
      pageSize,
      verdict ?? "all",
      reason ?? "all",
      search ?? "",
      marketplaceCategory ?? "all",
      magentoCategoryLeaf ?? "all",
      gapsOnly ?? false,
      setAsideOnly ?? false,
      nokCohort ?? "all",
    ],
    enabled: !!slug,
    staleTime: RUN_DETAIL_STALE_MS,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const result = await apiFetch<RunFeedPreviewResponse>(routes.runs.feedPreview({ slug }), {
        query: {
          limit: String(pageSize),
          offset: String(pageParam),
          ...(verdict ? { verdict } : {}),
          ...(reason ? { reason } : {}),
          ...(search ? { q: search } : {}),
          ...(marketplaceCategory ? { mpCategory: marketplaceCategory } : {}),
          ...(magentoCategoryLeaf ? { magCategory: magentoCategoryLeaf } : {}),
          ...(gapsOnly ? { gaps: "1" } : {}),
          ...(setAsideOnly ? { setAside: "1" } : {}),
          ...(nokCohort ? { cohort: nokCohort } : {}),
        },
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    getNextPageParam: (lastPage) =>
      lastPage.pagination?.hasMore
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
    refetchInterval: (query) => {
      if (query.state.status === "error") {
        return false
      }
      const freshness = previewFreshnessSinceRecovery(
        client,
        slug,
        previewFreshness(query.state.data?.pages ?? []),
        query.state.dataUpdatedAt
      )
      return previewPollInterval(freshness ? [{ freshness }] : [])
    },
  })
  const freshness = previewFreshness(preview.data?.pages ?? [])
  const recovery = useRunPreviewRecovery(
    slug,
    freshness ? { freshness } : undefined,
    preview.error,
    preview.dataUpdatedAt
  )
  return {
    ...preview,
    data: restrictedPreviewData(preview.data, recovery.freshness),
    recovery,
  }
}

export function useRunSkuPreview(slug: string, configSku: string) {
  return useQuery({
    queryKey: queryKeys.runSkuPreview(slug, configSku),
    enabled: Boolean(slug && configSku),
    queryFn: async () => {
      const result = await apiFetch<RunSkuPreviewResponse>(
        routes.runs.skuPreview({ slug, configSku })
      )
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
  })
}

// Archive / unarchive are bodyless POSTs. On success we invalidate both the
// runs list (the run appears/disappears from the default view) and the run
// detail queries (the header reflects the new archived state).
function useRunArchiveMutation(action: "archive" | "unarchive") {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (slug: string): Promise<ListingRun> => {
      const result = await apiFetch<{ run: ListingRun }>(routes.runs[action]({ slug }), {
        method: "POST",
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value.run
    },
    onSuccess: (_run, slug) => {
      void qc.invalidateQueries({ queryKey: ["runs"] })
      void qc.invalidateQueries({ queryKey: ["run", slug] })
    },
  })
}

export const useArchiveRun = () => useRunArchiveMutation("archive")
export const useUnarchiveRun = () => useRunArchiveMutation("unarchive")

type ValidateRunResult = {
  gapCount: number
  buildBlocked: boolean
  rowCount: number
  configSkuCount: number
  simpleCount: number | null
  /** Eligible variants with no marketplace offer yet, from the live snapshot.
   *  Null when the snapshot couldn't be read. */
  offersNeededCount: number | null
  verdicts: { ok: number; nok: number }
  masterCategory: string | null
}

// Validate the whole run: the server derives + persists the per-SKU OK/NOK
// verdict, gap count, and master category. Invalidating the run + list queries
// (a prefix that also covers the feed-preview / OK-NOK tab queries) refreshes
// the "Gaps" badge and the verdict tabs everywhere from the persisted value.
export function useValidateRun(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (): Promise<ValidateRunResult> => {
      const result = await apiFetch<ValidateRunResult>(routes.runs.validate({ slug }), {
        method: "POST",
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["runs"] })
      void qc.invalidateQueries({ queryKey: ["run", slug] })
    },
  })
}

// Set / edit / clear a product's DURABLE set-aside note (issue #122) — the first
// web mutation for set-aside. Keyed on the product (config SKU + marketplace),
// so it survives re-runs; the slug only supplies the marketplace. An empty note
// records a clear (history preserved server-side). On success we invalidate the
// run's set-aside list so the drill-down re-reads the note.
export function useSetAsideNote(slug: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: {
      configSku: string
      note: string
    }): Promise<SetAsideNoteResponse> => {
      const result = await apiFetch<SetAsideNoteResponse>(routes.runs.setAsideNote({ slug }), {
        method: "POST",
        body: input,
      })
      if (!result.ok) {
        throw new Error(result.error.message)
      }
      return result.value
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.runSetAside(slug) })
    },
  })
}
